import { useCallback, useEffect, useState } from "react";
import type { AiGrantAccess } from "@excalidraw-ms/shared";
import { Button } from "@excalidraw-ms/shared-ui";

interface ShareWithAiDialogProps {
  /** Drawing title, shown so the user can confirm what they are handing over. */
  title: string;
  /** Existing active code for this drawing, or null when nothing is shared yet. */
  code: string | null;
  /**
   * What the user can do with this drawing — and therefore the ceiling on what
   * an assistant holding the code can do. Reported, never chosen: a grant names
   * a drawing, it does not carry permission.
   */
  access: AiGrantAccess | null;
  /** Mint the code. Idempotent while active: re-sharing returns the same code. */
  onShare: () => Promise<void>;
  /** Start the revoke confirmation. Only offered once a code exists. */
  onRevoke?: () => void;
  /**
   * Open the connection instructions (ux-rules § 15g). Offered only once a code
   * exists — that is the moment the user has something to paste and no assistant
   * to paste it into.
   */
  onConnect?: () => void;
  onClose: () => void;
}

/**
 * "Share with AI" — hand a single drawing to an assistant (design D9).
 *
 * There is deliberately no view/edit chooser. The assistant acts as the user,
 * so SharePoint Embedded decides every read and write against the user's own
 * access. Offering a level here would promise capability the user may not have
 * (a viewer cannot grant edit) while claiming a restriction we cannot enforce
 * (the same token reaches /api/drawings without a grant). So the dialog states
 * what will happen instead of pretending to control it.
 */
export default function ShareWithAiDialog({
  title,
  code,
  access,
  onShare,
  onRevoke,
  onConnect,
  onClose,
}: ShareWithAiDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  useEffect(() => {
    if (copyState === "idle") return;
    const timer = setTimeout(() => setCopyState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [copyState]);

  const handleCreate = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onShare();
    } catch {
      setError("The code could not be created. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  };

  const capability = access
    ? access.canEdit
      ? "You can edit this drawing, so an assistant with this code can read it and save changes to it."
      : "You can only view this drawing, so an assistant with this code can read it but cannot change it."
    : "An assistant with this code can do exactly what you can do with this drawing — no more.";

  return (
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="false"
      data-app-usage-surface="sharing"
    >
      <div
        style={dialogStyle}
        onClick={(event) => event.stopPropagation()}
        data-testid="share-with-ai-dialog"
      >
        <h2 style={titleStyle}>Share with AI</h2>
        <p style={subtitleStyle}>{title}</p>

        <div style={accessRowStyle} data-testid="share-ai-access">
          <span style={accessBadgeStyle}>
            {access ? (access.canEdit ? "Can edit" : "Can view") : "Your access"}
          </span>
          <span style={accessTextStyle}>{capability}</span>
        </div>

        {code ? (
          <>
            <label style={labelStyle}>Code</label>
            <div style={rowStyle}>
              <input readOnly value={code} style={inputStyle} data-testid="share-ai-code" />
              <Button
                variant="primary"
                size="compact"
                onClick={() => void handleCopy()}
                data-testid="share-ai-copy"
              >
                {copyState === "copied" ? "Copied!" : copyState === "error" ? "Retry" : "Copy code"}
              </Button>
            </div>
          </>
        ) : (
          <div style={createRowStyle}>
            <Button
              variant="primary"
              size="compact"
              onClick={() => void handleCreate()}
              disabled={busy}
              style={busy ? { cursor: "progress" } : undefined}
              data-testid="share-ai-create"
            >
              {busy ? "Creating…" : "Create code"}
            </Button>
          </div>
        )}

        {error && (
          <p style={errorStyle} role="alert" data-testid="share-ai-error">
            {error}
          </p>
        )}

        <p style={noteStyle} data-testid="share-ai-note">
          {code
            ? "Paste this code into an assistant to let it work on this drawing — it never needs to know anything else about your account. The code keeps working until you stop access, and you can come back here any time to copy it again."
            : "Creating a code lets an assistant open this one drawing, and nothing else. You can copy it again later, or stop access at any time."}
        </p>

        {code && onConnect && (
          <p style={connectRowStyle}>
            <button
              type="button"
              onClick={onConnect}
              style={linkButtonStyle}
              data-testid="share-ai-connect"
            >
              First time? Connect an AI assistant
            </button>
          </p>
        )}

        {code && onRevoke && (
          <div style={footerStyle}>
            <Button
              variant="danger"
              size="compact"
              onClick={onRevoke}
              data-testid="share-ai-revoke"
            >
              Stop AI access
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  background: "var(--island-bg-color)",
  borderRadius: 12,
  border: "1px solid var(--dialog-border-color)",
  boxShadow: "var(--exms-shadow-modal)",
  padding: 24,
  fontFamily:
    "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  color: "var(--text-primary-color)",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 20,
  fontWeight: 700,
};

const subtitleStyle: React.CSSProperties = {
  margin: "6px 0 0",
  color: "var(--color-text-secondary)",
  fontSize: 15,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const accessRowStyle: React.CSSProperties = {
  marginTop: 20,
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "12px 14px",
  borderRadius: 10,
  background: "var(--color-surface-low)",
  border: "1px solid var(--dialog-border-color)",
};

const accessBadgeStyle: React.CSSProperties = {
  flex: "none",
  padding: "4px 10px",
  borderRadius: 999,
  background: "var(--color-primary-light)",
  border: "1px solid var(--dialog-border-color)",
  color: "var(--color-on-primary-soft)",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const accessTextStyle: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--color-text-secondary)",
  textAlign: "left",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginTop: 20,
  marginBottom: 8,
  fontSize: 14,
  fontWeight: 600,
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  alignItems: "center",
};

const createRowStyle: React.CSSProperties = {
  display: "flex",
  marginTop: 20,
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--input-bg-color)",
  color: "var(--text-primary-color)",
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: "0.08em",
};

const errorStyle: React.CSSProperties = {
  margin: "14px 0 0",
  color: "var(--exms-danger-text)",
  fontSize: 14,
};

const noteStyle: React.CSSProperties = {
  marginTop: 18,
  marginBottom: 0,
  paddingTop: 16,
  borderTop: "1px solid var(--dialog-border-color)",
  color: "var(--color-text-secondary)",
  fontSize: 14,
  lineHeight: 1.5,
};

const connectRowStyle: React.CSSProperties = {
  margin: "12px 0 0",
};

const linkButtonStyle: React.CSSProperties = {
  padding: 0,
  border: "none",
  background: "none",
  color: "var(--color-primary)",
  fontSize: 14,
  fontWeight: 600,
  textDecoration: "underline",
  cursor: "pointer",
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 20,
};
